import React, { useEffect, useMemo, useState } from 'react';
import { Box, Text, TextInput } from 'grommet';
import { FieldWeight } from '@helpers/FormFields';
import { ClaimedPlayer } from '@dtov1/shd';

type Props = {
  values: ClaimedPlayer | undefined;
  updateValues: (value: Record<string, unknown>) => void;
};

const parseHeight = (h?: string): { feet: string; inches: string } => {
  if (!h || !h.includes("'")) {
    return { feet: '', inches: '' };
  }
  const [ftRaw, rest] = h.split("'");
  const inchesRaw = (rest || '').replace('"', '');
  return {
    feet: (ftRaw ?? '').replace(/\D/g, ''),
    inches: (inchesRaw ?? '').replace(/\D/g, ''),
  };
};

const composeHeight = (feet: string, inches: string): string => {
  const ft = feet?.trim() ?? '';
  const inch = inches?.trim() ?? '';
  if (!ft && !inch) {
    return '';
  }
  return `${ft}'${inch}"`;
};

const PlayerBiometrics: React.FC<Props> = ({ values, updateValues }) => {
  const initial = useMemo(() => parseHeight(values?.height), [values?.height]);
  const [feet, setFeet] = useState<string>(initial.feet);
  const [inches, setInches] = useState<string>(initial.inches);

  useEffect(() => {
    setFeet(initial.feet);
    setInches(initial.inches);
  }, [initial.feet, initial.inches]);

  useEffect(() => {
    const height = composeHeight(feet, inches);
    updateValues({ height });
  }, [feet, inches, updateValues]);

  const onFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFeet(onlyNumbers);
  };

  const onInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 2);
    setInches(onlyNumbers);
  };

  const onInchesBlur = () => {
    if (inches === '') {
      return;
    }
    const num = Math.max(0, Math.min(11, Number(inches)));
    if (String(num) !== inches) {
      setInches(String(num));
    }
  };

  return (
    <>
      <Box gap="xsmall" margin={{ vertical: 'xsmall' }}>
        <Text size="large" color="dark-3">
          Height
        </Text>

        <Box direction="row" gap="xsmall" align="center">
          <TextInput
            autoComplete="nope"
            id="height-feet-id"
            placeholder="5"
            type="number"
            inputMode="numeric"
            pattern="\d*"
            value={feet}
            onChange={onFeetChange}
            plain
          />
          <Text color="text-weak">ft</Text>

          <TextInput
            autoComplete="nope"
            id="height-inches-id"
            placeholder="5"
            type="number"
            inputMode="numeric"
            pattern="\d*"
            value={inches}
            onChange={onInchesChange}
            onBlur={onInchesBlur}
            plain
          />
          <Text color="text-weak">in</Text>
        </Box>
      </Box>

      <FieldWeight />
    </>
  );
};

export default PlayerBiometrics;
